import * as React from "react";

import { Slot } from "@radix-ui/react-slot";
import { type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { trackEvent } from "~/lib/analytics";
import { cn } from "~/lib/utils";

import { buttonVariants } from "./button-utils";

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
    isLoading?: boolean;
    analyticsId?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            size,
            asChild = false,
            isLoading = false,
            children,
            analyticsId,
            onClick,
            ...props
        },
        ref,
    ) => {
        const Comp = asChild ? Slot : "button";

        const handleClick = React.useCallback(
            (
                e:
                    | React.MouseEvent<HTMLButtonElement>
                    | React.TouchEvent<HTMLButtonElement>,
            ) => {
                // AIDEV-NOTE: Use click only; touchend+click double-fired actions on iOS.
                // Get button text or icon name
                let buttonText = analyticsId;
                let iconType = "";

                if (!buttonText) {
                    if (typeof children === "string") {
                        buttonText = children;
                    } else {
                        // Check if children is a single element
                        try {
                            const child = React.Children.only(children);
                            if (React.isValidElement(child)) {
                                // Check for data-component-name attribute (icon-only buttons)
                                iconType =
                                    child.props["data-component-name"] || "";
                            }
                        } catch {
                            // Multiple children - that's fine
                        }
                        // AIDEV-NOTE: Always fall through to textContent for asChild buttons
                        // (e.g., <Button asChild><Link>View Form</Link></Button>)
                        buttonText =
                            e.currentTarget.textContent?.trim() || "unknown";
                    }
                }

                trackEvent("clicked button", {
                    text: analyticsId || buttonText || iconType,
                    icon: iconType,
                });

                // Call the original onClick handler if it exists
                return onClick?.(e as React.MouseEvent<HTMLButtonElement>);
            },
            [children, analyticsId, onClick],
        );

        return (
            <Comp
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                disabled={isLoading || props.disabled}
                onClick={handleClick}
                data-testid={analyticsId}
                {...props}
            >
                {isLoading ? (
                    <>
                        <Loader2 className="size-4 animate-spin" />
                        {children}
                    </>
                ) : variant === "magic" || variant === "magic-gradient" ? (
                    // AIDEV-NOTE: Both magic variants wrap children in gradient span so
                    // bg-clip-text applies rainbow text treatment instead of plain orange.
                    // Icons get explicit purple-600 since text-transparent hides SVGs.
                    <span className="flex items-center gap-[inherit] bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent [&_svg]:text-purple-600">
                        {children}
                    </span>
                ) : (
                    children
                )}
            </Comp>
        );
    },
);
Button.displayName = "Button";

export { Button, buttonVariants };
